import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Paintbrush, LayoutTemplate, Type, FileJson, Loader2, Download, CheckCircle2 } from 'lucide-react';
import { apiClient } from '@dukene/shared';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';

interface Theme {
  id: string;
  name: string;
  description: string;
  previewImage: string | null;
  currentVersion: string;
  isSystemTheme: boolean;
  price: number;
  author: { firstName: string | null; lastName: string | null } | null;
}

export default function ThemeMarketplace() {
  const [themes, setThemes] = useState<Theme[]>([]);
  const [loading, setLoading] = useState(true);
  const [installingId, setInstallingId] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchThemes();
  }, []);

  const fetchThemes = async () => {
    try {
      const data = await apiClient.get<Theme[]>('/marketplace/themes');
      setThemes(data || []);
    } catch (error) {
      console.error('Failed to fetch themes:', error);
      toast.error('Failed to load themes');
    } finally {
      setLoading(false);
    }
  };

  const handleInstall = async (themeId: string) => {
    setInstallingId(themeId);
    try {
      const data = await apiClient.post<{ storeThemeId: string }>(`/merchant/themes/${themeId}/install`);
      toast.success('Successfully installed theme to your store.');
      navigate(`/store/themes/${data.storeThemeId}/editor`);
    } catch (error: any) {
      toast.error(error.message || 'Failed to install theme');
    } finally {
      setInstallingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Theme Marketplace</h2>
          <p className="text-muted-foreground">Discover premium designs and templates to build your perfect store.</p>
        </div>
      </div>

      <Tabs defaultValue="themes" className="w-full flex-col">
        <TabsList className="grid w-full grid-cols-4 md:w-[600px] mb-6">
          <TabsTrigger value="themes" className="flex items-center gap-2">
            <Paintbrush className="h-4 w-4" /> Themes
          </TabsTrigger>
          <TabsTrigger value="components" className="flex items-center gap-2">
            <Type className="h-4 w-4" /> Components
          </TabsTrigger>
          <TabsTrigger value="pages" className="flex items-center gap-2">
            <FileJson className="h-4 w-4" /> Pages
          </TabsTrigger>
          <TabsTrigger value="templates" className="flex items-center gap-2">
            <LayoutTemplate className="h-4 w-4" /> Templates
          </TabsTrigger>
        </TabsList>

        <TabsContent value="themes" className="space-y-4">
          {loading ? (
            <div className="flex justify-center p-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : themes.length === 0 ? (
            <div className="text-center p-12 border rounded-lg bg-muted/30 border-dashed">
              <Paintbrush className="mx-auto h-12 w-12 text-muted-foreground mb-4 opacity-50" />
              <h3 className="text-lg font-medium">No themes available</h3>
              <p className="text-muted-foreground">Check back later for new themes.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {themes.map((theme) => (
                <Card key={theme.id} className="flex flex-col overflow-hidden">
                  <div className="aspect-video bg-muted relative flex items-center justify-center">
                    {theme.previewImage ? (
                      <img
                        src={theme.previewImage}
                        alt={theme.name}
                        className="object-cover w-full h-full"
                      />
                    ) : (
                      <Paintbrush className="h-12 w-12 text-muted-foreground/30" />
                    )}
                    {theme.isSystemTheme && (
                      <Badge className="absolute top-3 left-3 bg-primary hover:bg-primary/90">
                        <CheckCircle2 className="h-3 w-3 mr-1" /> Official
                      </Badge>
                    )}
                    <Badge variant="secondary" className="absolute top-3 right-3 font-mono">
                      v{theme.currentVersion}
                    </Badge>
                  </div>
                  <CardHeader>
                    <div className="flex justify-between items-start gap-4">
                      <div>
                        <CardTitle className="text-xl">{theme.name}</CardTitle>
                        <CardDescription className="mt-1">
                          By {theme.author?.firstName || 'Dukene Team'}
                        </CardDescription>
                      </div>
                      <div className="font-semibold whitespace-nowrap">
                        {theme.price === 0 ? 'Free' : `$${theme.price}`}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="flex-1 flex flex-col justify-between gap-6">
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {theme.description || 'A beautiful theme for your store.'}
                    </p>
                    <Button 
                      className="w-full"
                      onClick={() => handleInstall(theme.id)}
                      disabled={installingId === theme.id}
                    >
                      {installingId === theme.id ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Download className="mr-2 h-4 w-4" />
                      )}
                      {installingId === theme.id ? 'Installing...' : 'Install Theme'}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {['components', 'pages', 'templates'].map((tab) => (
          <TabsContent key={tab} value={tab} className="mt-0">
            <div className="text-center p-12 border rounded-lg bg-muted/30 border-dashed mt-4">
              <h3 className="text-lg font-medium capitalize">{tab} coming soon</h3>
              <p className="text-muted-foreground mt-2">
                We're building a massive library of {tab}. Stay tuned!
              </p>
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
